import React from "react"
import type { Kpis, Metric } from "~/api/portfolio_summaries"
import { formatCurrency, formatTvpi, formatIrr } from "./utils"

interface KpiStripProps {
  kpis: Kpis
}

const KPI_LABELS: Record<keyof Kpis, string> = {
  total_nav: "Total NAV",
  net_irr_itd: "Net IRR (ITD)",
  blended_moic: "Blended MOIC",
  total_pl_q: "Total P&L (Q)",
  net_cash_flow_q: "Net Cash Flow (Q)",
}

const formatValue = (metric: Metric): string => {
  if (metric.value == null) return "—"
  switch (metric.format) {
    case "currency":
      return formatCurrency(metric.value)
    case "multiple":
      return formatTvpi(metric.value)
    case "percent":
      return formatIrr(metric.value)
    default:
      return String(metric.value)
  }
}

const formatDelta = (metric: Metric): string | null => {
  if (metric.delta_bps != null) {
    const sign = metric.delta_bps >= 0 ? "+" : ""
    return `${sign}${metric.delta_bps} bps QoQ`
  }
  if (metric.delta_pct != null) {
    const sign = metric.delta_pct >= 0 ? "+" : ""
    return `${sign}${metric.delta_pct}% QoQ`
  }
  return null
}

const KpiCard: React.FC<{ label: string; metric: Metric }> = ({
  label,
  metric,
}) => {
  const delta = formatDelta(metric)
  return (
    <div className="b-portfolio-summary-kpi">
      <div className="b-portfolio-summary-kpi-label">{label}</div>
      <div className="b-portfolio-summary-kpi-value">{formatValue(metric)}</div>
      {delta && (
        <div
          className={`b-portfolio-summary-kpi-delta b-portfolio-summary-kpi-delta--${
            metric.dir ?? "flat"
          }`}
        >
          {metric.dir === "up" ? "▲" : metric.dir === "down" ? "▼" : ""} {delta}
        </div>
      )}
      {metric.methodology === "mv_weighted_avg_of_series_irr" && (
        <div className="b-portfolio-summary-kpi-note">MV-weighted avg. of series IRR</div>
      )}
    </div>
  )
}

const KpiStrip: React.FC<KpiStripProps> = ({ kpis }) => (
  <div className="b-portfolio-summary-kpis">
    {(Object.keys(KPI_LABELS) as (keyof Kpis)[]).map((key) => (
      <KpiCard key={key} label={KPI_LABELS[key]} metric={kpis[key]} />
    ))}
  </div>
)

export default KpiStrip
